/**
 * PlayShop — Mode collection TCG (binary owned/not).
 *
 * Sur les pages collection.tcg-set (/collections/pokemon-*) :
 *   - Bouton étoile burger (pos-4) toggle le mode collection (body.is-collection-mode)
 *   - En mode collection : le clic sur les boutons variants (quick-add cart) appelle
 *     l'Edge function `collection-toggle` au lieu d'ajouter au panier
 *   - Les variants possédées ont la classe .is-owned (checkmark visuel)
 *   - Persisté via localStorage (par user)
 *
 * Prérequis :
 *   - bouton [data-tcgset-toggle-collection] avec :
 *       data-customer-id (vide si pas connecté)
 *       data-set-id (ex sv10.5b)
 *       data-licence-slug (ex pokemon)
 */

(function () {
  const SUPABASE_FN_BASE = 'https://ipvzpgwgujamtzztxvcu.supabase.co/functions/v1';
  // ⚠ CACHE_VERSION : bump à chaque changement de structure/logique de rendu chips raretés.
  // L'inline anti-flash dans collection.tcg-set.liquid vérifie cached.v === CACHE_VERSION,
  // sinon skip le render depuis cache (évite FOUC chip qui apparaît en retard).
  // Doit être identique à la valeur dans collection.tcg-set.liquid.
  const RARITY_CACHE_VERSION = 'v11-2026-05-26-star-after-ex';

  // ─── Render chips raretés dans le hero du set ───
  // Cache localStorage par set_id → render SYNC au load (anti-flash), fetch derrière pour sync.
  function renderRarityChipsFromData(rarities, container, icons) {
    if (!rarities?.length) return;
    const order = ['commune', 'peu commune', 'rare', 'holo rare', 'rare holographique', 'radieux rare', 'radieux', 'double rare',
      'prime',
      'g', 'fb', 'c', 'gl', '4',
      'niv.x',
      'v', 'vmax', 'vstar',
      'gx', 'escouade', 'prisme étoile',
      'ex', 'turbo',
      'légende',
      'star',
      'zarbi',
      'full art', 'alternative', 'rainbow', 'gold', 'secrète', 'tg', 'rc',
      'ultra-chimère',
      'holographique', 'gg', 'ultra rare',
      'illustration rare', 'magnifique', 'magnifique rare', 'chromatique rare',
      'shiny', 'shiny rare', 'shiny v', 'shiny rare v', 'shiny vmax', 'shiny rare vmax', 'shiny gold',
      'noir blanc rare', 'rare noir blanc',
      'illustration spéciale rare', 'chromatique ultra rare', 'shiny ultra rare', 'méga attaque rare',
      'high-tech rare', 'high-tecg rare', 'hyper rare', 'méga hyper rare',
      'secret'];

    // ═════ Normalisation noms : labels affichés sur les chips ═════
    function normalizeRarityName(name) {
      const ln = String(name).toLowerCase();
      if (ln === 'holo rare v') return 'V';
      if (ln === 'holo rare vmax') return 'VMAX';
      if (ln === 'holo rare vstar') return 'VSTAR';
      // swsh4.5 Shiny Vault : shorten "Shiny rare X" → "Shiny X"
      if (ln === 'shiny rare') return 'Shiny';
      if (ln === 'shiny rare v') return 'Shiny V';
      if (ln === 'shiny rare vmax') return 'Shiny VMAX';
      // Magnifique rare sur SV121/SV122 → "Shiny Gold" (différencier de "Magnifique" main set)
      if (ln === 'magnifique rare') return 'Shiny Gold';
      // Radieux Rare : chip dédié "Radieux" (les Pokémon Radieux, ex Dracaufeu Radieux) — plus mergé dans "Rare".
      if (ln === 'radieux rare') return 'Radieux';
      return name;
    }
    // Preserve original rarity BEFORE normalization (used in data-rarity for filter)
    rarities.forEach((r) => { r._original = r.name; r.name = normalizeRarityName(r.name); });
    // Merge doublons post-normalisation, track originals for filter
    const mergedMap = {};
    rarities.forEach((r) => {
      const k = String(r.name).toLowerCase();
      if (mergedMap[k]) {
        mergedMap[k].count += (r.count || 0);
        if (!mergedMap[k]._filterOriginals) mergedMap[k]._filterOriginals = [];
        if (mergedMap[k]._filterOriginals.indexOf(r._original) === -1) mergedMap[k]._filterOriginals.push(r._original);
      } else {
        mergedMap[k] = { name: r.name, count: r.count || 0, _filterOriginals: [r._original] };
      }
    });
    rarities = Object.keys(mergedMap).map((k) => mergedMap[k]);

    // ═════ Agrégation chips Rare-like (rare + holo rare + rare holo + rare holographique + radieux rare → 1 chip "Rare") ═════
    // Note 2026-05-25 : "Rare Holo" (avec espace, ordre HGSS) ajouté — sinon chip séparé sur hgss1-4.
    const rareLikeSet = { 'rare': 1, 'holo rare': 1, 'rare holo': 1, 'rare holographique': 1 };
    let rareAcc = null;
    const aggregated = [];
    rarities.forEach((r) => {
      const ln = String(r.name).toLowerCase();
      if (rareLikeSet[ln]) {
        if (!rareAcc) { rareAcc = { name: 'Rare', count: 0, _raritiesList: [] }; aggregated.push(rareAcc); }
        rareAcc.count += (r.count || 0);
        rareAcc._raritiesList.push(r.name);
      } else {
        aggregated.push(r);
      }
    });
    rarities = aggregated;
    // Hide chip Escouade. Chip GX reste visible comme V/VMAX/VSTAR.
    const HIDDEN_CHIPS = { 'escouade': 1 };
    rarities = rarities.filter(r => !HIDDEN_CHIPS[String(r.name).toLowerCase()]);

    rarities.sort((a, b) => {
      const ia = order.indexOf(a.name.toLowerCase());
      const ib = order.indexOf(b.name.toLowerCase());
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
    // HTML aligné EXACTEMENT sur le inline anti-flash du template (même format, même title).
    // L'état is-active est appliqué APRÈS via classList (zero flash, n'invalide pas l'idempotence).
    const newHtml = rarities.map((r) => {
      const icon = icons[r.name.toLowerCase()];
      // Chips spéciaux : Shiny V / Shiny VMAX = "SHINY" + icon V/VMAX, Magnifique = icon seul
      const lnName = String(r.name).toLowerCase();
      let displayText = String(r.name).toUpperCase();
      if (lnName === 'shiny v' || lnName === 'shiny vmax') { displayText = 'SHINY'; }
      // Tous les chips à 22px height. Horizontal assets (gx, ultra-chimère) gardent ratio.
      const isHorizAsset = (lnName === 'gx' || lnName === 'ultra-chimère' || lnName === 'ex' || lnName === 'turbo' || lnName === 'légende' || lnName === 'fb' || lnName === 'gl');
      // Note 2026-05-25 : pas de loading="lazy" — chips above-the-fold dans hero set.
      // Le lazy retardait le fetch des assets neufs (légende, etc.) → flash visible.
      const iconImg = icon
        ? (isHorizAsset
          ? `<img src="${icon}" alt="" style="height:22px;width:auto;">`
          : `<img src="${icon}" alt="" width="22" height="22">`)
        : '';
      const textSpan = `<span class="ps-tcg-set__rarity-text">${displayText}</span>`;
      // Niv.X chip : "NIV." + X doré (équivalent badge title Platine LV.X)
      const nivxSpan = `<span class="ps-tcg-set__rarity-text ps-tcg-set__rarity-text--nivx">NIV.</span><span class="ps-tcg-set__rarity-text ps-tcg-set__rarity-text--nivx ps-tcg-set__rarity-text--nivx-x">X</span>`;
      // EX-era "ex" lowercase chip : preserve casing, no asset (vintage Pokémon-ex, 2003-2007)
      const exLowerSpan = `<span class="ps-tcg-set__rarity-text" style="text-transform:none;">ex</span>`;
      let iconHtml;
      if (lnName === 'shiny v' || lnName === 'shiny vmax') {
        iconHtml = textSpan + iconImg;
      } else if (lnName === 'niv.x') {
        iconHtml = nivxSpan;
      } else if (r.name === 'ex') {
        // EX-era lowercase ex : skip image even if icon exists
        iconHtml = exLowerSpan;
      } else if (icon) {
        iconHtml = iconImg;
      } else {
        iconHtml = textSpan;
      }
      // Filter use ORIGINAL rarity name (not normalized)
      const filterPrimary = (r._filterOriginals && r._filterOriginals[0]) || r.name;
      const aggList = (r._raritiesList && r._raritiesList.length > 1) ? r._raritiesList
                    : (r._filterOriginals && r._filterOriginals.length > 1 ? r._filterOriginals : null);
      const agg = aggList ? ` data-aggregated-rarities="${aggList.join('||').replace(/"/g, '&quot;')}"` : '';
      return `<button type="button" class="ps-tcg-set__rarity-chip" data-rarity="${String(filterPrimary).replace(/"/g, '&quot;')}"${agg} title="${r.name} : ${r.count}">${iconHtml}<span class="ps-tcg-set__rarity-count">${r.count}</span></button>`;
    }).join('');
    // Idempotence : skip si le HTML est identique (évite flash quand inline anti-flash a déjà rendu).
    if (container.innerHTML.trim() !== newHtml.trim()) {
      container.innerHTML = newHtml;
    }
    // Apply is-active state via classList (post-render, pas de flash de re-paint).
    if (window.psIsFilterActive) {
      container.querySelectorAll('.ps-tcg-set__rarity-chip').forEach((chip) => {
        const r = chip.dataset.rarity;
        if (r) chip.classList.toggle('is-active', window.psIsFilterActive('meta_tcgrarete', r));
      });
    }
  }

  // Click handler : toggle filter rareté via API window.psToggleFilter.
  // Chip agrégé (Rare-like) : toggle TOUTES les raretés listées dans data-aggregated-rarities.
  document.addEventListener('click', (e) => {
    const chip = e.target.closest('.ps-tcg-set__rarity-chip');
    if (!chip) return;
    if (typeof window.psToggleFilter !== 'function') return;
    const aggList = chip.dataset.aggregatedRarities;
    if (aggList) {
      // Toggle multi : on prend le state du premier item pour décider on/off, puis on aligne les autres
      const list = aggList.split('||');
      const firstActive = window.psIsFilterActive && window.psIsFilterActive('meta_tcgrarete', list[0]);
      // Si chip pas actif → activer toutes les sous-raretés ; si actif → désactiver toutes
      list.forEach((r) => {
        const isOn = window.psIsFilterActive && window.psIsFilterActive('meta_tcgrarete', r);
        if (firstActive && isOn) window.psToggleFilter('meta_tcgrarete', r);  // turn off
        else if (!firstActive && !isOn) window.psToggleFilter('meta_tcgrarete', r);  // turn on
      });
      chip.classList.toggle('is-active', !firstActive);
    } else {
      const rarity = chip.dataset.rarity;
      if (!rarity) return;
      const active = window.psToggleFilter('meta_tcgrarete', rarity);
      chip.classList.toggle('is-active', active);
    }
  });

  // Sync chips active state si filtres changent ailleurs (drawer/checkboxes)
  document.addEventListener('ps-filter-changed', (e) => {
    if (e.detail?.key !== 'meta_tcgrarete') return;
    document.querySelectorAll('.ps-tcg-set__rarity-chip').forEach((chip) => {
      const r = chip.dataset.rarity;
      if (!r) return;
      chip.classList.toggle('is-active', window.psIsFilterActive && window.psIsFilterActive('meta_tcgrarete', r));
    });
  });

  async function renderRarityChips() {
    const container = document.querySelector('[data-ps-rarities]');
    if (!container) return;
    const setId = container.dataset.setId;
    if (!setId) return;
    let icons = {};
    try {
      const iconScript = document.querySelector('[data-ps-rarity-icons]');
      icons = JSON.parse(iconScript?.textContent || '{}');
    } catch (e) {}

    const cacheKey = `ps-rarity-counts-${setId}`;
    // Apply cache SYNC pour anti-flash — UNIQUEMENT si version match (sinon stale → skip render,
    // évite FOUC chip qui apparaît en retard après fetch Supabase).
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
      if (cached?.rarities?.length && cached.v === RARITY_CACHE_VERSION) {
        renderRarityChipsFromData(cached.rarities, container, icons);
      }
    } catch (e) {}

    // Fetch derrière pour sync
    try {
      const r = await fetch(`${SUPABASE_FN_BASE}/set-rarity-counts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ set_id: setId, licence_slug: 'pokemon' }),
      });
      const data = await r.json();
      const rarities = data.rarities || [];
      if (rarities.length) {
        try { localStorage.setItem(cacheKey, JSON.stringify({ rarities, v: RARITY_CACHE_VERSION, ts: Date.now() })); } catch (e) {}
        renderRarityChipsFromData(rarities, container, icons);
      }
    } catch (e) {
      console.warn('[PS TCG] rarity counts fetch failed', e);
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderRarityChips);
  } else {
    renderRarityChips();
  }

  const STORAGE_KEY = 'ps-tcgset-collection-mode';


  const toggleBtn = document.querySelector('[data-tcgset-toggle-collection]');
  // Note : ce JS n'est chargé QUE sur les pages où le mode collection est pertinent
  // (sets, Pokédex, pages "Ma Collection"). On ne bail PAS si aucun badge au chargement :
  // sur le dashboard suivi, les cartes (donc les badges) arrivent en différé (fetch card-mini) →
  // un MutationObserver les repeint à l'injection. On gate uniquement sur "connecté".

  // customerId : depuis le bouton set si présent, sinon variable globale window.PS_CUSTOMER_ID
  // (Pokédex / page Ma Collection, où il n'y a pas de bouton set).
  const customerId = (toggleBtn && toggleBtn.dataset.customerId) || (window.PS_CUSTOMER_ID ? String(window.PS_CUSTOMER_ID) : '');
  // setId / licenceSlug : présents sur page set (filtre la liste + barres progression).
  // Vides ailleurs → collection-list renvoie TOUTES les cartes possédées (matching par variant id).
  const setId = (toggleBtn && toggleBtn.dataset.setId) || '';
  const licenceSlug = (toggleBtn && toggleBtn.dataset.licenceSlug) || '';
  const isLoggedIn = customerId !== '';

  // ─── VISITEUR : le badge devient une INVITATION, plus un bouton mort ────────────────────
  // Avant : on masquait les badges et on s'arrêtait là (« sinon le 3e badge serait inerte »).
  // Deux problèmes : (1) le masquage ne rattrape QUE les badges déjà dans le DOM — les cartes
  // chargées en différé gardaient le leur (72 badges inertes constatés sur une page de set) ;
  // (2) surtout, c'est LE moment où le visiteur manifeste l'envie d'utiliser l'outil, et on lui
  // répondait par rien. Or la majorité du trafic arrive de Google sur une fiche/un set, jamais
  // par l'accueil : c'était le seul endroit où on demandait l'inscription.
  // Maintenant : le badge reste visible et son clic ouvre le pitch. Délégation sur `document`
  // → marche aussi pour les cartes injectées après coup, sans MutationObserver.
  if (!isLoggedIn) {
    document.addEventListener('click', (e) => {
      const badge = e.target.closest('[data-ps-collect-badge]');
      if (!badge) return;
      e.preventDefault();
      e.stopPropagation(); // ne pas suivre le lien de la carte en dessous
      if (window.psGuestSignup) window.psGuestSignup('collection-badge');
    }, true);
    return;
  }

  // Cache key local (par user + licence + set) pour appliquer les owned visuels INSTANTANÉMENT
  // au reload (pas d'attente du fetch Supabase). Sync via fetch derrière.
  const CACHE_KEY = `ps-owned-${customerId}-${licenceSlug}-${setId}`;

  const body = document.body;
  const ownedVariantIds = new Set(); // Shopify variant IDs the user owns
  const ownedQty = new Map(); // shopify_variant_id -> quantité possédée (>=1)
  const ownedPrice = new Map(); // shopify_variant_id -> prix d'achat unitaire (€)

  // Helpers prix (format FR avec virgule, parse tolérant virgule/point)
  function fmtPrice(n) { return (Math.round(n * 100) / 100).toFixed(2).replace('.', ','); }
  function parsePrice(s) {
    const v = parseFloat(String(s).replace(',', '.').replace(/[^\d.]/g, ''));
    return isNaN(v) ? null : v;
  }

  // Init le set depuis le cache localStorage (avant fetch)
  if (isLoggedIn) {
    try {
      const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || '[]');
      cached.forEach((id) => ownedVariantIds.add(Number(id)));
    } catch (e) {}
  }

  function persistOwnedCache() {
    if (!isLoggedIn) return;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(Array.from(ownedVariantIds)));
    } catch (e) {}
  }

  // ─── Toast helper (équivalent au hub TCG) ───
  let toastEl = null;
  let toastTimer = null;
  function showToast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'ps-tcghub-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    clearTimeout(toastTimer);
    requestAnimationFrame(() => toastEl.classList.add('is-visible'));
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 3500);
  }

  // ─── Update visuel des boutons variants selon owned state ───
  function refreshOwnedVisuals() {
    // Multi-variant buttons : <form class="ps-card__variant-form"> contains <input name="id" value="VARIANT_ID">
    document.querySelectorAll('.ps-card__variant-form').forEach((form) => {
      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;
      const variantId = Number(idInput.value);
      const owned = ownedVariantIds.has(variantId);
      const btn = form.querySelector('button.ps-card__cta');
      if (btn) btn.classList.toggle('is-owned', owned);
      // .is-owned sur la FORME → la CSS bascule bouton↔stepper en mode collection
      form.classList.toggle('is-owned', owned);
      const valEl = form.querySelector('[data-ps-qty-val]');
      if (valEl) valEl.textContent = String(ownedQty.get(variantId) || 1);
      renderBuyprice(variantId);
    });
    // Single-variant : <form class="ps-card__form"> contains <input name="id">
    document.querySelectorAll('.ps-card__form').forEach((form) => {
      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;
      const variantId = Number(idInput.value);
      const btn = form.querySelector('button.ps-card__cta');
      if (!btn) return;
      btn.classList.toggle('is-owned', ownedVariantIds.has(variantId));
    });
    refreshCollectBadges();
  }

  // ─── Badge collection (3e badge, toujours visible) : état +/✓/partiel par carte ───
  // owned = nb de variants possédés sur cette carte ; total = nb de variants.
  // tous possédés → .is-owned (✓) ; certains → .is-partial (data-count "N/M") ; aucun → "+".
  function refreshCollectBadges() {
    document.querySelectorAll('.ps-card').forEach((card) => {
      const badge = card.querySelector('[data-ps-collect-badge]');
      if (!badge) return;
      const forms = card.querySelectorAll('.ps-card__variant-form, .ps-card__form');
      let total = 0, owned = 0, qtySum = 0;
      forms.forEach((f) => {
        const idInput = f.querySelector('input[name="id"]');
        if (!idInput) return;
        total++;
        const vid = Number(idInput.value);
        // qtySum = somme des exemplaires de TOUTES les variantes de cette carte (classique+reverse+holo).
        // Une carte scellée = 1 seule variante → sa propre quantité. Deux .ps-card distinctes pour
        // carte vs scellé → jamais de mélange des univers (chacune ne somme que ses propres forms).
        if (ownedVariantIds.has(vid)) { owned++; qtySum += (ownedQty.get(vid) || 1); }
      });
      const allOwned = total > 0 && owned === total;
      const partial = owned > 0 && owned < total;
      badge.classList.toggle('is-owned', allOwned);
      badge.classList.toggle('is-partial', partial);
      if (partial) badge.setAttribute('data-count', owned + '/' + total);
      else badge.removeAttribute('data-count');
      badge.setAttribute('aria-label', allOwned ? 'Dans ma collection' : (partial ? ('Collection ' + owned + '/' + total) : 'Ajouter à ma collection'));
      // 4e badge quantité (×N) — visible seulement si possédée (qtySum >= 1), ×1 inclus (repère visuel constant).
      const qtyBadge = card.querySelector('[data-ps-qty-badge]');
      if (qtyBadge) {
        if (qtySum >= 1) {
          qtyBadge.textContent = '×' + qtySum;
          qtyBadge.classList.add('is-visible');
          qtyBadge.setAttribute('aria-hidden', 'false');
          qtyBadge.setAttribute('aria-label', qtySum + (qtySum > 1 ? ' exemplaires' : ' exemplaire') + ' dans ma collection');
        } else {
          qtyBadge.textContent = '';
          qtyBadge.classList.remove('is-visible');
          qtyBadge.setAttribute('aria-hidden', 'true');
          qtyBadge.removeAttribute('aria-label');
        }
      }
    });
  }

  // ─── Fetch owned list ───
  async function fetchOwnedList() {
    if (!isLoggedIn) return;
    try {
      // Fetch en parallèle : cartes (collection-list) + scellé (sealed-collection-list).
      // Les 2 endpoints sont dissociés (cf. principe Lorenzo) mais leur shape de sortie est identique
      // (owned_variants[] avec shopify_variant_id + quantity + purchase_price_eur) → merge trivial.
      const [cardsRes, sealedRes] = await Promise.all([
        fetch(`${SUPABASE_FN_BASE}/collection-list`, {
          method: 'POST',
          // Identité = `x-ps-token` (métafield Shopify → window.PS_AUTH_TOKEN). Le
          // `shopify_customer_id` du body est ignoré côté serveur : il ne prouvait rien.
          // ⚠️ Ne PAS ajouter ce header à l'appel `set-rarity-counts` de ce fichier :
          // fn publique (catalogue), son CORS ne l'autorise pas → preflight bloqué.
          headers: { 'Content-Type': 'application/json', 'x-ps-token': window.PS_AUTH_TOKEN || '' },
          body: JSON.stringify({
            shopify_customer_id: Number(customerId),
            licence_slug: licenceSlug,
            set_id: setId,
          }),
        }).then((r) => r.json()).catch(() => ({})),
        fetch(`${SUPABASE_FN_BASE}/sealed-collection-list`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-ps-token': window.PS_AUTH_TOKEN || '' },
          body: JSON.stringify({ shopify_customer_id: Number(customerId) }),
        }).then((r) => r.json()).catch(() => ({})),
      ]);
      ownedVariantIds.clear();
      ownedQty.clear();
      ownedPrice.clear();
      const merge = (list) => (list || []).forEach((v) => {
        if (v.shopify_variant_id) {
          const id = Number(v.shopify_variant_id);
          ownedVariantIds.add(id);
          ownedQty.set(id, v.quantity != null ? Number(v.quantity) : 1);
          if (v.purchase_price_eur != null) ownedPrice.set(id, Number(v.purchase_price_eur));
        }
      });
      merge(cardsRes.owned_variants);
      merge(sealedRes.owned_variants);
      persistOwnedCache(); // Sync localStorage avec server
      refreshOwnedVisuals();
    } catch (e) {
      console.warn('[PS TCG] fetch owned list failed', e);
    }
  }

  // ─── Bump progression (catégorie déduite de la lettre du variant) ───
  // Appelé UNIQUEMENT au passage possédé↔non possédé (pas à chaque +/− de quantité,
  // car la progression compte les cartes distinctes, pas les exemplaires).
  function bumpProgressForVariant(variantId, delta) {
    try {
      const form = document.querySelector(`.ps-card__variant-form input[value="${variantId}"]`)?.closest('form');
      const letter = form?.querySelector('.ps-card__cta-variant-name')?.textContent?.trim() || '';
      const card = form?.closest('.ps-card');
      const localId = (card?.dataset.psCardLocalId || card?.dataset.psProductCardNum || '').toUpperCase();
      const isGallery = /^(GG|TG)/.test(localId);
      const isZarbi = (card?.dataset.psMetaTcgrarete || '').toLowerCase() === 'zarbi';
      bumpProgressLocal(letter.toUpperCase(), delta, isGallery, isZarbi, card);
    } catch (e) {}
  }

  // ─── Helper : route vers la bonne edge fn selon le type de produit ───
  // Cartes individuelles → collection-set ; Scellé (Display/Booster/Coffret/…) → sealed-collection-set.
  // La card porte data-ps-collect-fn="sealed" via le snippet Liquid quand ps_is_sealed.
  function pickCollectFn(variantId) {
    const input = document.querySelector(`.ps-card__variant-form input[value="${variantId}"]`);
    const card = input?.closest('.ps-card');
    return card?.dataset.psCollectFn === 'sealed' ? 'sealed-collection-set' : 'collection-set';
  }

  // ─── Fixe la quantité d'un variant via collection-set (idempotent) ───
  // qty <= 0 → retrait (owned=false) ; qty >= 1 → possédé avec cette quantité.
  async function setQuantity(variantId, qty, price) {
    if (!isLoggedIn) { showToast('Connectez-vous pour suivre votre collection.'); return; }
    qty = Math.max(0, Math.floor(qty));
    const wasOwned = ownedVariantIds.has(variantId);
    const prevQty = ownedQty.get(variantId) || 0;
    // Optimistic
    if (qty <= 0) { ownedVariantIds.delete(variantId); ownedQty.delete(variantId); ownedPrice.delete(variantId); }
    else {
      ownedVariantIds.add(variantId); ownedQty.set(variantId, qty);
      if (price != null && !isNaN(price)) {
        ownedPrice.set(variantId, price); // prix fourni (saisie utilisateur)
      } else if (!ownedPrice.has(variantId)) {
        // 1er ajout sans prix → snapshot optimiste du prix marché affiché. Le serveur fait
        // le même snapshot ; on capte sa valeur exacte dans la réponse.
        const bp = document.querySelector(`[data-ps-buyprice][data-variant-id="${variantId}"]`);
        const mk = bp ? parseFloat(bp.dataset.market || '') : NaN;
        if (!isNaN(mk)) ownedPrice.set(variantId, mk);
      }
    }
    refreshOwnedVisuals();
    persistOwnedCache();
    const nowOwned = qty > 0;
    if (nowOwned !== wasOwned) bumpProgressForVariant(variantId, nowOwned ? 1 : -1);

    try {
      const r = await fetch(`${SUPABASE_FN_BASE}/${pickCollectFn(variantId)}`, {
        method: 'POST',
        // pickCollectFn → `collection-set` ou `sealed-collection-set` : les deux sont jeton-only.
        headers: { 'Content-Type': 'application/json', 'x-ps-token': window.PS_AUTH_TOKEN || '' },
        body: JSON.stringify({ shopify_customer_id: Number(customerId), shopify_variant_id: variantId, quantity: qty }),
      });
      const data = await r.json();
      if (data.error) {
        if (wasOwned) { ownedVariantIds.add(variantId); ownedQty.set(variantId, prevQty || 1); }
        else { ownedVariantIds.delete(variantId); ownedQty.delete(variantId); }
        refreshOwnedVisuals(); persistOwnedCache();
        showToast(data.error === 'variant not in catalog' ? 'Carte pas encore synchronisée - réessayer plus tard.' : 'Erreur, réessaie.');
        return;
      }
      if (data.owned) {
        ownedVariantIds.add(variantId); ownedQty.set(variantId, data.quantity || qty);
        // ⚠️ NE PAS appliquer aveuglément le prix renvoyé (bug signalé par Florian, 17/07).
        // Cet appel n'envoie un prix QUE si `price` est fourni. Sans prix, le serveur renvoie
        // quand même le prix qu'il a EN BASE — souvent le snapshot de la cote fait au 1er ajout.
        // Or le champ « prix d'achat » ne se valide qu'au blur : cliquer « + » juste après avoir
        // saisi un prix lance DEUX requêtes concurrentes ({qty,prix} au blur + {qty} au clic).
        // La réponse de celle du « + » porte le prix d'AVANT → elle écrasait la saisie par la cote.
        // Règle : on n'accepte le prix du serveur que si c'est NOUS qui l'avons envoyé, ou si on
        // n'en a encore aucun localement (1er ajout = on récupère le snapshot exact du serveur).
        const jAiFourniLePrix = price != null && !isNaN(price);
        if (data.purchase_price_eur != null && (jAiFourniLePrix || !ownedPrice.has(variantId))) {
          ownedPrice.set(variantId, Number(data.purchase_price_eur));
        }
      }
      else { ownedVariantIds.delete(variantId); ownedQty.delete(variantId); ownedPrice.delete(variantId); }
      refreshOwnedVisuals(); persistOwnedCache();
      fetchProgress();
      notifyCollectionChanged(); // → dashboard recalcule KPIs (valeur/plus-value) en quasi-live
    } catch (e) {
      if (wasOwned) { ownedVariantIds.add(variantId); ownedQty.set(variantId, prevQty || 1); }
      else { ownedVariantIds.delete(variantId); ownedQty.delete(variantId); }
      refreshOwnedVisuals(); persistOwnedCache();
      showToast('Connexion impossible, réessaie.');
    }
  }

  // Clic sur le bouton variant : non possédé → ajoute 1 exemplaire ; possédé → retire.
  function toggleOwned(variantId) {
    var wasOwned = ownedVariantIds.has(variantId);
    if (!wasOwned) { try { window.psTrack && window.psTrack('tool_collection_add', { variant_id: variantId, licence: licenceSlug, set: setId }); } catch (_) {} }
    setQuantity(variantId, wasOwned ? 0 : 1);
  }

  // ─── Notifie le reste de la page qu'une carte de la collection a changé (qté ou prix) ───
  // Le dashboard suivi (playshop-suivi-licence.js) écoute pour recalculer les KPIs en quasi-live.
  function notifyCollectionChanged() {
    try { document.dispatchEvent(new CustomEvent('ps:collection-changed')); } catch (_) {}
  }

  // ─── Affiche le prix d'achat enregistré dans le champ d'un variant ───
  // Le bloc est révélé par la CSS quand le variant-form a .is-owned.
  // Pas de plus-value sur la carte (Lorenzo 2026-06-16) — elle vivra dans le dashboard.
  function renderBuyprice(variantId) {
    // ⚠️ TOUTES les instances : une même carte peut être affichée 2× (ex dashboard suivi : Top 5 + Derniers Ajouts).
    // querySelectorAll (pas querySelector) → l'édition du PA sur une instance synchronise l'autre.
    const wraps = document.querySelectorAll(`[data-ps-buyprice][data-variant-id="${variantId}"]`);
    if (!wraps.length) return;
    const owned = ownedVariantIds.has(variantId);
    const price = ownedPrice.has(variantId) ? Number(ownedPrice.get(variantId)) : null;
    const valStr = (price != null && !isNaN(price)) ? fmtPrice(price) : '';
    wraps.forEach((wrap) => {
      wrap.classList.toggle('is-owned', owned);
      const input = wrap.querySelector('[data-ps-buyprice-input]');
      if (!input || document.activeElement === input) return; // ne pas écraser le champ en cours de saisie
      input.value = valStr;
    });
  }

  // ─── Fixe le prix d'achat d'un variant possédé (collection-set, optimistic + rollback) ───
  async function setPrice(variantId, price) {
    if (!isLoggedIn || !ownedVariantIds.has(variantId)) return;
    const qty = ownedQty.get(variantId) || 1;
    const prev = ownedPrice.has(variantId) ? ownedPrice.get(variantId) : null;
    ownedPrice.set(variantId, price);
    renderBuyprice(variantId);
    try {
      const r = await fetch(`${SUPABASE_FN_BASE}/${pickCollectFn(variantId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-ps-token': window.PS_AUTH_TOKEN || '' },
        body: JSON.stringify({ shopify_customer_id: Number(customerId), shopify_variant_id: variantId, quantity: qty, purchase_price_eur: price }),
      });
      const data = await r.json();
      if (data.error) {
        if (prev != null) ownedPrice.set(variantId, prev); else ownedPrice.delete(variantId);
        renderBuyprice(variantId);
        showToast('Prix non enregistré, réessaie.');
        return;
      }
      if (data.purchase_price_eur != null) ownedPrice.set(variantId, Number(data.purchase_price_eur));
      renderBuyprice(variantId);
      notifyCollectionChanged(); // → dashboard recalcule la plus-value en quasi-live
    } catch (e) {
      if (prev != null) ownedPrice.set(variantId, prev); else ownedPrice.delete(variantId);
      renderBuyprice(variantId);
    }
  }

  // ─── User progress (barres Classique / Reverse / Pokéball / Masterball en mode collection) ───
  const progressContainer = document.querySelector('[data-ps-user-progress]');
  // ⚠ 'foil' (Lorcana) DOIT être présent sinon renderProgress() ne met jamais à jour la barre/compteur
  //   Foil au clic (bump optimiste écrit foil_owned mais la row n'était pas itérée → maj seulement au reload).
  //   Sans data-total côté Liquid pour 'foil' → total vient de collection-stats (foil_total via edgeTotal).
  // ⛔ 25/09/2026 : 'set' = la barre 1 des series Naruto, LE SET entier (une ligne par carte,
  //   secretes comprises, toutes finitions confondues). 'classic' ne pouvait plus la porter
  //   depuis que Shinobi Shiren set 2 a 78 cartes SANS version classique.
  const PROGRESS_CATS = ['classic', 'set', 'reverse', 'pokeball', 'masterball', 'type', 'rocket', 'cosmos', 'gallery', 'zarbi', 'edition1', 'edition2', 'foil', 'fullart', 'holo'];
  const progressCache = {};
  PROGRESS_CATS.forEach((c) => { progressCache[c + '_total'] = 0; progressCache[c + '_owned'] = 0; });

  function renderProgress() {
    if (!progressContainer) return;
    PROGRESS_CATS.forEach((cat) => {
      const row = progressContainer.querySelector(`[data-progress-row="${cat}"]`);
      if (!row) return;
      // data-total = dénominateur Liquid (source de vérité — set_total / set_reverse_actual).
      // ⚠ 2026-05-22 : on respecte data-total=0 comme valeur INTENTIONNELLE (cacher la barre).
      //   Cas concret : ME02.5 reverse_actual=0 pour cacher la barre Reverse même si BDD a 38 reverses.
      //   Fallback Edge fn UNIQUEMENT si data-total est absent (NaN après parseInt).
      const liquidTotal = parseInt(row.dataset.total, 10);
      const edgeTotal = progressCache[cat + '_total'] || 0;
      const total = !isNaN(liquidTotal) ? liquidTotal : edgeTotal;
      const owned = progressCache[cat + '_owned'] || 0;
      const pct = total ? Math.min(100, (owned / total) * 100) : 0;
      const fill = row.querySelector('.ps-tcg-set__user-progress-fill');
      // Set CSS var --bar-pct : utilisé par keyframe au load et par width transition une fois .is-ready
      fill.style.setProperty('--bar-pct', pct + '%');
      row.querySelector(`[data-progress-value="${cat}"]`).textContent = `${owned}/${total}`;
      row.querySelector('.ps-tcg-set__user-progress-bar').setAttribute('aria-valuenow', String(Math.round(pct)));
      // Row cachée par défaut (style=display:none HTML) → reveal SEULEMENT si total > 0
      // (évite le flash sur les sets sans pokeball/masterball/reverse)
      if (row.hasAttribute('data-hide-if-zero')) {
        row.style.display = total > 0 ? '' : 'none';
      }
    });
  }

  // Après animation initiale, swap chaque fill en mode transition (updates fluides sans re-animer 0→target à chaque clic)
  if (progressContainer) {
    progressContainer.querySelectorAll('.ps-tcg-set__user-progress-fill').forEach((fill) => {
      fill.addEventListener('animationend', () => fill.classList.add('is-ready'), { once: true });
    });
  }

  async function fetchProgress() {
    if (!isLoggedIn || !progressContainer) return;
    try {
      const r = await fetch(`${SUPABASE_FN_BASE}/collection-stats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-ps-token': window.PS_AUTH_TOKEN || '' },
        body: JSON.stringify({
          shopify_customer_id: Number(customerId),
          licence_slug: licenceSlug,
          set_id: setId,
        }),
      });
      const data = await r.json();
      const s = (data.sets || []).find((x) => x.set_id === setId);
      if (s) {
        PROGRESS_CATS.forEach((cat) => {
          progressCache[cat + '_total'] = s[cat + '_total'] || 0;
          progressCache[cat + '_owned'] = s[cat + '_owned'] || 0;
        });
        try { localStorage.setItem(`ps-progress-${customerId}-${licenceSlug}-${setId}`, JSON.stringify(s)); } catch (e) {}
      }
      renderProgress();
    } catch (e) {
      console.warn('[PS TCG] fetch progress failed', e);
    }
  }

  // Init cache depuis localStorage (anti-flash) + FETCH au load (bloc Ma Progression
  // toujours visible quand connecté, indépendant du mode collection)
  if (isLoggedIn && progressContainer) {
    try {
      const cached = JSON.parse(localStorage.getItem(`ps-progress-${customerId}-${licenceSlug}-${setId}`) || 'null');
      if (cached) { Object.assign(progressCache, cached); renderProgress(); }
    } catch (e) {}
    // Toujours render au load (utile pour révéler les rows reverse/pokeball/masterball
    // avec leur dénominateur Liquid via data-total, même sans cache)
    renderProgress();
    // Puis fetch derrière pour sync les valeurs owned avec Supabase
    fetchProgress();
  }

  // Update local (optimiste) au toggle owned — catégorie déduite du label du button.
  // ⚠ Ordre important : check 'TR' AVANT 'T' (Team Rocket avant Type, sinon 'TR'.startsWith('T') = true).
  // Si isGallery=true (cards GG/TG), bump dans 'gallery' au lieu de la catégorie variant.
  function bumpProgressLocal(variantLabelLetter, delta, isGallery, isZarbi, cardEl) {
    if (!progressContainer) return;
    let cat = 'classic';
    if (isZarbi) cat = 'zarbi';
    else if (isGallery) cat = 'gallery';
    else if (variantLabelLetter === 'R') cat = 'reverse';
    else if (variantLabelLetter === 'P') cat = 'pokeball';
    else if (variantLabelLetter === 'M') cat = 'masterball';
    else if (variantLabelLetter === 'TR') cat = 'rocket';
    else if (variantLabelLetter === 'CH') cat = 'cosmos';
    else if (variantLabelLetter === 'T') cat = 'type';
    // Pré-EX vintage (Base/Gym/Neo) : variant labels E1 / ED.2 → progress dédié
    else if (variantLabelLetter === 'E1') cat = 'edition1';
    else if (variantLabelLetter === 'ED.2') cat = 'edition2';
    // Lorcana : 'F' (Brillant/Foil) → barre Foil. Les secrètes (foil-only) sont catégorisées
    // 'Normale' côté serveur → le refetch collection-stats corrige le bump optimiste.
    else if (variantLabelLetter === 'F') cat = 'foil';
    // Naruto Mythos : 'FA' (Full Art) et 'H' (Holo) sur C/UC → barres dédiées Full Art / Holo.
    else if (variantLabelLetter === 'FA') cat = 'fullart';
    else if (variantLabelLetter === 'H') cat = 'holo';
    const key = cat + '_owned';
    progressCache[key] = Math.max(0, (progressCache[key] || 0) + delta);
    // ⛔ 25/09/2026 - la barre 1 des series Naruto ('set') compte les CARTES, pas les finitions :
    //   elle ne bouge qu'au passage 0 ↔ 1 finition possedee sur la carte, sinon une carte prise
    //   en Full Art puis en Holo la ferait monter de deux. refreshOwnedVisuals() a deja tourne
    //   quand on arrive ici, donc .is-owned sur les formes porte l'etat APRES le clic.
    if (licenceSlug === 'naruto' && cardEl) {
      const nOwned = cardEl.querySelectorAll('.ps-card__variant-form.is-owned').length;
      if (delta > 0 && nOwned === 1) progressCache.set_owned = Math.max(0, (progressCache.set_owned || 0) + 1);
      else if (delta < 0 && nOwned === 0) progressCache.set_owned = Math.max(0, (progressCache.set_owned || 0) - 1);
    }
    renderProgress();
  }

  // ─── Mode collection PER-CARTE (.ps-card.is-collecting) ───
  // Active/désactive le morph d'UNE carte (≠ body.is-collection-mode global du mode scellé).
  // En mode : boutons variants gris "+" / vert "✓" + stepper + bloc prix, numéro/titre masqués.
  function setCardCollecting(card, active) {
    if (!card) return;
    card.classList.toggle('is-collecting', active);
    // Rend les boutons variants cliquables même sur cartes OOS (on track la possession,
    // pas la dispo panier). On preserve l'état initial via data-was-disabled.
    card.querySelectorAll('.ps-card__cta--variant').forEach((btn) => {
      if (active) {
        if (btn.hasAttribute('disabled')) {
          btn.dataset.wasDisabled = '1';
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        }
      } else if (btn.dataset.wasDisabled === '1') {
        btn.setAttribute('disabled', '');
        btn.setAttribute('aria-disabled', 'true');
        delete btn.dataset.wasDisabled;
      }
    });
    // Peint prix + plus-value des rows prix de cette carte
    if (active) {
      card.querySelectorAll('[data-ps-buyprice]').forEach((row) => {
        const vid = Number(row.dataset.variantId);
        if (vid) renderBuyprice(vid);
      });
    }
  }

  // ─── Init ───
  // Mode collection désormais PER-CARTE (transitoire, non persisté) : aucune restauration
  // au load. Seuls les visuels owned (badges ✓/partiel + cache localStorage) sont peints.

  // Apply visuels SYNC au load depuis le cache localStorage (ownedVariantIds déjà
  // populé en haut de ce script). Plus rapide que d'attendre le fetch Supabase.
  // Le fetch derrière sync l'état réel (si user a ajouté carte sur un autre device).
  if (isLoggedIn && ownedVariantIds.size > 0) {
    // requestAnimationFrame pour s'assurer que le DOM est ready
    requestAnimationFrame(refreshOwnedVisuals);
  } else {
    // Pas de cache : pose au moins l'état "+" par défaut sur les badges
    requestAnimationFrame(refreshCollectBadges);
  }
  // Le badge collection est TOUJOURS visible (indicateur d'état) → on fetch la liste owned
  // au load dès qu'on est connecté, même hors mode collection, pour peindre les ✓/partiels.
  if (isLoggedIn) {
    fetchOwnedList(); // Sync derrière (peuple ownedVariantIds + refreshOwnedVisuals + badges)
  }

  // ─── Click handler du bouton étoile (UNIQUEMENT si présent = pages set) ───
  // ⚠ toggleBtn est null sur Pokédex / page Ma Collection → SANS ce garde, le throw empêcherait
  // l'enregistrement de TOUS les handlers suivants (clic badge inclus) → le + naviguerait au lieu d'ajouter.
  // L'étoile burger (set pages) devient un RACCOURCI vers la page « Ma Collection » de la LICENCE
  // courante (data-suivi-url = /pages/suivi-{pokemon|lorcana|one-piece|naruto}), pas le hub.
  // Fallback /pages/suivi-collection si l'attribut manque.
  if (toggleBtn) {
    // « Ajouter » = mode multi-sélection (Lorenzo 12/08) : au lieu de naviguer vers le dashboard,
    // on bascule TOUTES les cartes en mode collecte d'un coup (réutilise setCardCollecting, prouvé).
    // Un clic sur les boutons variant marque possédé ; re-clic sur « Ajouter » sort du mode.
    toggleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const active = !document.body.classList.contains('is-collection-mode');
      document.body.classList.toggle('is-collection-mode', active);
      toggleBtn.setAttribute('aria-pressed', String(active));
      toggleBtn.classList.toggle('is-active', active);
      document.querySelectorAll('.ps-card').forEach((card) => setCardCollecting(card, active));
    });
  }

  // ─── Clic sur le 3e badge collection d'une carte (toujours visible) ───
  // Entre en mode collection sans ouvrir le burger → les boutons variants deviennent
  // les +/✓ d'ajout (mécanisme prouvé). Capture phase + preventDefault : le badge est
  // DANS le <a.ps-card__link>, donc on bloque la navigation vers la fiche.
  document.addEventListener('click', (e) => {
    const badge = e.target.closest('[data-ps-collect-badge]');
    if (!badge) return;
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) { showToast('Connectez-vous pour suivre votre collection.'); return; }
    const card = badge.closest('.ps-card');
    if (!card) return;
    const willBeActive = !card.classList.contains('is-collecting');
    setCardCollecting(card, willBeActive);
  }, true);

  // ─── Intercept clicks sur les boutons variants en mode collection ───
  // Multi-variant forms (.ps-card__variant-form)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.ps-card__variant-form button, .ps-card__form button.ps-card__cta');
    if (!btn) return;
    // Les boutons du stepper quantité (− / +) sont gérés par leur propre handler.
    if (btn.hasAttribute('data-ps-qty-minus') || btn.hasAttribute('data-ps-qty-plus')) return;
    // Per-carte : on n'intercepte que si LA carte de ce bouton est en mode collection.
    const card = btn.closest('.ps-card');
    if (!card || !card.classList.contains('is-collecting')) return;
    const form = btn.closest('form');
    if (!form) return;
    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;
    e.preventDefault();
    e.stopPropagation();
    const variantId = Number(idInput.value);
    if (variantId) toggleOwned(variantId);
  }, true); // capture phase pour intercepter avant Shopify cart add

  // ─── Stepper quantité (− / +) : fixe la quantité du variant possédé ───
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-ps-qty-minus]');
    const plus = e.target.closest('[data-ps-qty-plus]');
    if (!minus && !plus) return;
    e.preventDefault();
    e.stopPropagation();
    const form = (minus || plus).closest('.ps-card__variant-form');
    if (!form) return;
    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;
    const variantId = Number(idInput.value);
    const cur = ownedQty.get(variantId) || 0;
    setQuantity(variantId, plus ? cur + 1 : cur - 1);
  }, true);

  // ─── Champ Prix d'achat : commit au blur/change, plus-value recalculée ───
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-ps-buyprice-input]');
    if (!input) return;
    const row = input.closest('[data-ps-buyprice]');
    const variantId = Number(row?.dataset.variantId);
    if (!variantId) return;
    const v = parsePrice(input.value);
    if (v == null || v < 0) { renderBuyprice(variantId); return; } // invalide → restaure
    if (ownedVariantIds.has(variantId)) {
      setPrice(variantId, v); // déjà possédé → met juste à jour le prix
    } else {
      // saisir un prix sur un variant NON possédé → l'ajoute à la collection (qty 1) avec ce prix
      setQuantity(variantId, 1, v);
    }
  });
  // Enter dans le champ prix : ne PAS soumettre le form (sinon /cart/add) → blur (commit)
  document.addEventListener('keydown', (e) => {
    const input = e.target.closest('[data-ps-buyprice-input]');
    if (!input) return;
    if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
  });

  // ─── Repaint des cartes INJECTÉES en différé (dashboard suivi : Top 5 / Derniers Ajouts
  // sont fetchés via card-mini puis insérés par playshop-suivi-licence.js APRÈS le load).
  // Les handlers (clic badge, stepper, prix) sont délégués sur document → marchent déjà sur
  // les cartes injectées. Mais l'état (badge ✓/+/partiel + valeur prix) doit être (re)peint
  // quand de nouvelles .ps-card apparaissent. Debounce pour éviter le thrash. ───
  let _psRepaintTimer = null;
  const _psObserver = new MutationObserver((mutations) => {
    let cardAdded = false;
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType === 1 && (node.classList?.contains('ps-card') || node.querySelector?.('.ps-card'))) {
          cardAdded = true; break;
        }
      }
      if (cardAdded) break;
    }
    if (!cardAdded) return;
    clearTimeout(_psRepaintTimer);
    _psRepaintTimer = setTimeout(() => refreshOwnedVisuals(), 120);
  });
  _psObserver.observe(document.body, { childList: true, subtree: true });
})();
